import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Theme types
export const THEMES = {
  DARK: 'dark',
  LIGHT: 'light',
  SYSTEM: 'system'
};

// Theme context
const ThemeContext = createContext({
  theme: THEMES.LIGHT,
  resolvedTheme: THEMES.LIGHT,
  setTheme: () => {},
  toggleTheme: () => {}
});

// Local storage key
const THEME_STORAGE_KEY = 'crm-theme-preference';

// Check if current path is an admin route (but NOT login/auth pages)
const isAdminRoute = (pathname) => {
  // These auth pages should use light theme even under /admin
  const authPages = ['/admin/login', '/admin/forgot-password', '/admin/reset-password', '/admin/accept-invite'];
  if (authPages.some(page => pathname?.startsWith(page))) {
    return false; // Treat as public (light theme)
  }
  return pathname?.startsWith('/admin');
};

export const ThemeProvider = ({ children }) => {
  // Get current location to determine if we're on admin or public pages
  let currentPath = '/';
  try {
    // This works when inside Router context
    currentPath = window.location.pathname;
  } catch (e) {
    currentPath = '/';
  }
  
  const isAdmin = isAdminRoute(currentPath);
  
  const [theme, setThemeState] = useState(() => {
    // ALL PAGES: Default to LIGHT theme
    // Users can toggle to dark via theme toggle if they prefer
    if (typeof window !== 'undefined') {
      const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
      // Return stored preference if exists, otherwise default to LIGHT
      return storedTheme || THEMES.LIGHT;
    }
    return THEMES.LIGHT;
  });
  
  const [resolvedTheme, setResolvedTheme] = useState(THEMES.LIGHT);

  // Resolve system theme - default to light for consistency
  const getSystemTheme = useCallback(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches 
        ? THEMES.DARK 
        : THEMES.LIGHT;
    }
    return THEMES.LIGHT;
  }, []);

  // Apply theme to document
  const applyTheme = useCallback((themeValue, forceLight = false) => {
    const root = document.documentElement;
    
    // PUBLIC PAGES: Always force light theme
    const isAdminPath = isAdminRoute(window.location.pathname);
    const resolved = forceLight || !isAdminPath 
      ? THEMES.LIGHT 
      : (themeValue === THEMES.SYSTEM ? getSystemTheme() : themeValue);
    
    // Remove existing theme classes
    root.classList.remove('dark', 'light');
    
    // Add new theme class
    root.classList.add(resolved);
    
    // Update data attribute for CSS selectors
    root.setAttribute('data-theme', resolved);
    
    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', resolved === THEMES.DARK ? '#050505' : '#ffffff');
    }
    
    // Update apple status bar style
    const metaStatusBar = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
    if (metaStatusBar) {
      metaStatusBar.setAttribute('content', resolved === THEMES.DARK ? 'black-translucent' : 'default');
    }
    
    setResolvedTheme(resolved);
  }, [getSystemTheme]);

  // Set theme with persistence (only for admin routes)
  const setTheme = useCallback(async (newTheme) => {
    const isAdminPath = isAdminRoute(window.location.pathname);
    
    // Only allow theme changes on admin routes
    if (!isAdminPath) {
      applyTheme(THEMES.LIGHT, true);
      return;
    }
    
    setThemeState(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    applyTheme(newTheme);
    
    // Save to backend if user is logged in
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await fetch(`${API_URL}/api/auth/users/preferences`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ theme: newTheme })
        });
      } catch (error) {
        console.error('Failed to save theme preference:', error);
      }
    }
  }, [applyTheme]);

  // Toggle between dark and light (admin only)
  const toggleTheme = useCallback(() => {
    const isAdminPath = isAdminRoute(window.location.pathname);
    if (!isAdminPath) return;
    
    const newTheme = resolvedTheme === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK;
    setTheme(newTheme);
  }, [resolvedTheme, setTheme]);

  // Load theme from user preferences on mount (admin only)
  useEffect(() => {
    const loadUserPreference = async () => {
      const isAdminPath = isAdminRoute(window.location.pathname);
      
      // Public pages: always light theme
      if (!isAdminPath) {
        applyTheme(THEMES.LIGHT, true);
        return;
      }
      
      // Admin pages: check for user preference
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const res = await fetch(`${API_URL}/api/auth/users/preferences`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            if (data.theme && data.theme !== theme) {
              setThemeState(data.theme);
              localStorage.setItem(THEME_STORAGE_KEY, data.theme);
              applyTheme(data.theme);
            }
          }
        } catch (error) {
          // Use localStorage fallback
          console.log('Using localStorage theme preference');
        }
      }
    };
    
    loadUserPreference();
  }, []);

  // Apply theme on mount and when theme changes
  useEffect(() => {
    const isAdminPath = isAdminRoute(window.location.pathname);
    if (isAdminPath) {
      applyTheme(theme);
    } else {
      // Public pages: always force light theme
      applyTheme(THEMES.LIGHT, true);
    }
  }, [theme, applyTheme]);

  // Apply the correct theme for the CURRENT path (used by route sync + bfcache restore).
  const applyThemeForCurrentPath = useCallback(() => {
    const isAdminPath = isAdminRoute(window.location.pathname);
    if (isAdminPath) {
      const storedTheme = localStorage.getItem(THEME_STORAGE_KEY) || THEMES.LIGHT;
      applyTheme(storedTheme);
    } else {
      applyTheme(THEMES.LIGHT, true);
    }
  }, [applyTheme]);

  // Listen for route changes to update theme
  useEffect(() => {
    const handleRouteChange = () => applyThemeForCurrentPath();

    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', handleRouteChange);
    // Listen for bfcache restores (browser back/forward can restore a cached page
    // without re-running React effects — re-assert the correct theme here).
    const handlePageShow = () => applyThemeForCurrentPath();
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [applyThemeForCurrentPath]);

  // Listen for system theme changes (admin only)
  useEffect(() => {
    const isAdminPath = isAdminRoute(window.location.pathname);
    if (theme !== THEMES.SYSTEM || !isAdminPath) return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => applyTheme(THEMES.SYSTEM);
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, applyTheme]);

  // Prevent flash of wrong theme - always start with light for public pages
  useEffect(() => {
    const isAdminPath = isAdminRoute(window.location.pathname);
    const themeToApply = isAdminPath 
      ? (theme === THEMES.SYSTEM ? getSystemTheme() : theme)
      : THEMES.LIGHT;
    
    document.documentElement.classList.remove('dark', 'light');
    document.documentElement.classList.add(themeToApply);
    document.documentElement.setAttribute('data-theme', themeToApply);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme, toggleTheme, applyThemeForCurrentPath }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
